/**
 * useComments - Hook to integrate comments with the TipTap editor
 * Supports real-time collaboration via Yjs when collaborationEnabled is true
 */
import { useCallback, useEffect, useRef } from "react"
import { Editor } from "@tiptap/core"
import { useCommentsStore } from "stores/commentsStore"
import { Comment, CommentStatus, CreateReplyData } from "types/comments.types"
import { ApiStorageProvider, CommentsApiFunctions } from "services/comments/ApiStorageProvider"
import { useCommentsApi } from "hooks/api/useCommentsApi"
import {
  findCommentMarkPosition,
  getCommentIdsInEditor,
  findTextInDocument,
} from "features/QuestionnaireEditor/components/Editor/Extensions/CommentMarkExtension"

interface UseCommentsProps {
  editor: Editor | null
  projectId: string
  variantName: string
  userId: string
  userName: string
  owner: string
  enabled?: boolean
  /** Enable real-time collaboration for comments */
  collaborationEnabled?: boolean
  /** WebSocket URL for collaboration server */
  websocketUrl?: string
}

interface UseCommentsReturn {
  // State
  comments: Comment[]
  activeCommentId: string | null
  isPanelOpen: boolean
  isFormOpen: boolean
  formSelectedText: string
  formPosition: { x: number; y: number } | null
  isInitialized: boolean

  // Panel actions
  openPanel: () => void
  closePanel: () => void
  togglePanel: () => void

  // Form actions
  openCommentForm: () => void
  closeCommentForm: () => void

  // Comment actions
  addComment: (content: string) => void
  updateComment: (commentId: string, content: string) => void
  addReply: (commentId: string, content: string) => void
  deleteComment: (commentId: string) => void
  deleteReply: (commentId: string, replyId: string) => void
  setStatus: (commentId: string, status: CommentStatus) => void
  resolveComment: (commentId: string) => void

  // Navigation
  setActiveComment: (commentId: string | null) => void
  navigateToComment: (commentId: string) => void

  // Event handlers for extension
  handleCommentActivated: (commentId: string | null) => void
}

export const useComments = ({
  editor,
  projectId,
  variantName,
  userId,
  userName,
  owner,
  enabled = true,
  collaborationEnabled = false,
  websocketUrl,
}: UseCommentsProps): UseCommentsReturn => {
  const store = useCommentsStore()
  const previousCommentIdsRef = useRef<Set<string>>(new Set())
  const api = useCommentsApi()

  // Store API functions in a ref to avoid infinite loops from changing references
  const apiFunctionsRef = useRef<CommentsApiFunctions>({
    getCommentsById: api.getCommentsById,
    addCommentById: api.addCommentById,
    updateCommentById: api.updateCommentById,
    deleteCommentById: api.deleteCommentById,
    resolveCommentById: api.resolveCommentById,
    addReplyById: api.addReplyById,
    deleteReplyById: api.deleteReplyById,
  })

  // Update ref when api changes (but don't trigger re-renders)
  useEffect(() => {
    apiFunctionsRef.current = {
      getCommentsById: api.getCommentsById,
      addCommentById: api.addCommentById,
      updateCommentById: api.updateCommentById,
      deleteCommentById: api.deleteCommentById,
      resolveCommentById: api.resolveCommentById,
      addReplyById: api.addReplyById,
      deleteReplyById: api.deleteReplyById,
    }
  })

  // Initialize provider when projectId changes
  useEffect(() => {
    if (!enabled || !projectId) return

    const provider = new ApiStorageProvider({
      api: apiFunctionsRef.current,
      // Enable collaboration if both flag and websocketUrl are provided
      collaboration: collaborationEnabled && websocketUrl ? {
        websocketUrl,
        projectId,
      } : undefined,
    })
    store.initializeProvider(provider, projectId, variantName, owner)

    return () => {
      store.disposeProvider()
    }
  }, [projectId, variantName, owner, enabled, collaborationEnabled, websocketUrl])

  // Re-apply comment marks when comments are loaded from backend
  const hasAppliedMarksRef = useRef(false)

  // Helper function to apply comment decorations
  const applyCommentDecorations = useCallback(() => {
    if (!editor || !store.isInitialized) return

    const comments = store.comments.filter(
      (c) => !c.isDeleted && c.variantName === variantName && c.originalText
    )

    if (comments.length === 0) return

    // Check which comments already have marks in the editor
    const existingMarks = getCommentIdsInEditor(editor)

    comments.forEach((comment) => {
      // Skip if already marked
      if (existingMarks.has(comment.id)) return

      // Find the text in the document
      const position = findTextInDocument(
        editor,
        comment.originalText,
        comment.lineNumber
      )

      if (position) {
        // Apply comment mark at found position
        editor
          .chain()
          .setTextSelection({ from: position.from, to: position.to })
          .setComment(comment.id)
          .run()
      }
    })

    // Clear selection after applying marks
    editor.commands.blur()

    // Update tracking with all applied marks
    previousCommentIdsRef.current = getCommentIdsInEditor(editor)
  }, [editor, store.isInitialized, store.comments, variantName])

  // Initial application of comment marks
  useEffect(() => {
    if (!editor || !store.isInitialized || !enabled) return
    if (hasAppliedMarksRef.current) return // Only apply once per session

    // Small delay to ensure editor content is fully loaded
    const timeoutId = setTimeout(() => {
      applyCommentDecorations()
      hasAppliedMarksRef.current = true
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [editor, store.isInitialized, store.comments, variantName, enabled, applyCommentDecorations])

  // Re-sync comments when they're lost or when new remote comments arrive
  useEffect(() => {
    if (!editor || !store.isInitialized || !enabled) return
    if (!hasAppliedMarksRef.current) return // Initial application not done yet

    const comments = store.comments.filter(
      (c) => !c.isDeleted && c.variantName === variantName && c.originalText
    )

    // If store has comments that aren't yet marked in the editor, apply them
    const editorCommentIds = getCommentIdsInEditor(editor)
    const hasUnmarkedComments = comments.some((c) => !editorCommentIds.has(c.id))
    if (hasUnmarkedComments) {
      // Small delay to ensure editor is ready after extension changes
      const timeoutId = setTimeout(() => {
        applyCommentDecorations()
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [editor, store.isInitialized, store.comments, variantName, enabled, applyCommentDecorations])

  // Track comment marks in editor for deletion detection
  useEffect(() => {
    if (!editor || !enabled || !store.isInitialized) return

    const checkForDeletedComments = () => {
      const currentIds = getCommentIdsInEditor(editor)
      const previousIds = previousCommentIdsRef.current

      // If ALL comments disappeared at once and store still has comments,
      // this is likely due to extension recreation (e.g., highlighting toggle),
      // not actual text deletion. Re-sync instead of deleting.
      const storeComments = store.comments.filter(
        (c) => !c.isDeleted && c.variantName === variantName && c.originalText
      )

      if (previousIds.size > 0 && currentIds.size === 0 && storeComments.length > 0) {
        // Extension was likely recreated - re-sync comments instead of deleting
        setTimeout(() => {
          applyCommentDecorations()
        }, 100)
        return
      }

      // Find comments that existed before but are now gone (individual deletions)
      previousIds.forEach((commentId) => {
        if (!currentIds.has(commentId)) {
          // This comment's text was deleted
          const comment = store.getCommentById(commentId)
          if (comment && !comment.isDeleted) {
            store.deleteComment(commentId, userId, userName, "text_deleted")
          }
        }
      })

      previousCommentIdsRef.current = currentIds
    }

    // Initial scan
    previousCommentIdsRef.current = getCommentIdsInEditor(editor)

    // Check on every editor update
    const handleUpdate = () => {
      checkForDeletedComments()
    }

    editor.on("update", handleUpdate)

    return () => {
      editor.off("update", handleUpdate)
    }
  }, [editor, enabled, store.isInitialized, userId, userName, variantName, applyCommentDecorations])

  // Keyboard shortcut for adding comments (Ctrl+Alt+M)
  useEffect(() => {
    if (!editor || !enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.altKey && e.key.toLowerCase() === "m") {
        e.preventDefault()
        openCommentForm()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [editor, enabled])

  // Open comment form with current selection
  const openCommentForm = useCallback(() => {
    if (!editor) return

    const { from, to } = editor.state.selection
    if (from === to) {
      // No selection - can't add comment
      return
    }

    const selectedText = editor.state.doc.textBetween(from, to, " ")
    if (!selectedText.trim()) return

    // Get selection position for form placement
    const coords = editor.view.coordsAtPos(from)
    const position = {
      x: coords.left,
      y: coords.bottom + 10,
    }

    store.openForm(selectedText, position)
  }, [editor, store])

  // Add a new comment
  const addComment = useCallback(
    (content: string) => {
      if (!editor || !store.formSelectedText) return

      // Calculate line number for re-applying marks on reload
      const { from } = editor.state.selection
      let lineNumber = 1
      editor.state.doc.nodesBetween(0, from, (node) => {
        if (node.isBlock) lineNumber++
      })

      // Create comment in store
      const commentId = store.addComment({
        projectId,
        variantName,
        authorId: userId,
        authorName: userName,
        content,
        status: "open",
        originalText: store.formSelectedText,
        lineNumber,
      })

      if (commentId) {
        // Apply comment mark to selected text
        editor.chain().focus().setComment(commentId).run()

        // Update tracking
        previousCommentIdsRef.current.add(commentId)

        // Open panel and set active
        store.openPanel()
        store.setActiveComment(commentId)
      }

      store.closeForm()
    },
    [editor, store, projectId, variantName, userId, userName]
  )

  // Add reply to existing comment
  const addReply = useCallback(
    (commentId: string, content: string) => {
      const replyData: CreateReplyData = {
        commentId,
        authorId: userId,
        authorName: userName,
        content,
      }

      store.addReply(commentId, replyData)
    },
    [store, userId, userName]
  )

  // Delete comment
  const deleteComment = useCallback(
    (commentId: string) => {
      if (!editor) return

      // Remove decoration from editor (pass commentId directly)
      editor.commands.unsetComment(commentId)

      // Delete from store
      store.deleteComment(commentId, userId, userName, "manual_delete")

      // Update tracking
      previousCommentIdsRef.current.delete(commentId)

      // Clear active if it was this comment
      if (store.activeCommentId === commentId) {
        store.setActiveComment(null)
      }
    },
    [editor, store, userId, userName]
  )

  // Delete reply
  const deleteReply = useCallback(
    (commentId: string, replyId: string) => {
      store.deleteReply(commentId, replyId)
    },
    [store]
  )

  // Update comment content
  const updateComment = useCallback(
    (commentId: string, content: string) => {
      store.updateComment(commentId, { content })
    },
    [store]
  )

  // Change comment status
  const setStatus = useCallback(
    (commentId: string, status: CommentStatus) => {
      store.setStatus(commentId, status, userId, userName)
    },
    [store, userId, userName]
  )

  // Resolve comment
  const resolveComment = useCallback(
    (commentId: string) => {
      store.resolveComment(commentId, userId, userName)
    },
    [store, userId, userName]
  )

  // Navigate to comment in editor
  const navigateToComment = useCallback(
    (commentId: string) => {
      if (!editor) return

      const position = findCommentMarkPosition(editor, commentId)
      if (position) {
        // Scroll to position and select
        editor.chain().focus().setTextSelection(position.from).run()

        // Scroll into view
        const coords = editor.view.coordsAtPos(position.from)
        const editorElement = editor.view.dom
        const editorRect = editorElement.getBoundingClientRect()

        if (coords.top < editorRect.top || coords.bottom > editorRect.bottom) {
          editor.view.dom.scrollTo({
            top:
              coords.top -
              editorRect.top +
              editor.view.dom.scrollTop -
              editorRect.height / 2,
            behavior: "smooth",
          })
        }

        // Highlight the comment mark
        document
          .querySelectorAll(".comment-highlight.active")
          .forEach((el) => el.classList.remove("active"))

        const commentElements = document.querySelectorAll(
          `[data-comment-id="${commentId}"]`
        )
        commentElements.forEach((el) => el.classList.add("active"))
      }

      store.setActiveComment(commentId)
    },
    [editor, store]
  )

  // Handle comment activation from editor click
  const handleCommentActivated = useCallback(
    (commentId: string | null) => {
      store.setActiveComment(commentId)

      if (commentId) {
        // Open panel when clicking a comment
        store.openPanel()
      }
    },
    [store]
  )

  return {
    // State
    comments: store.comments,
    activeCommentId: store.activeCommentId,
    isPanelOpen: store.isPanelOpen,
    isFormOpen: store.isFormOpen,
    formSelectedText: store.formSelectedText,
    formPosition: store.formPosition,
    isInitialized: store.isInitialized,

    // Panel actions
    openPanel: store.openPanel,
    closePanel: store.closePanel,
    togglePanel: store.togglePanel,

    // Form actions
    openCommentForm,
    closeCommentForm: store.closeForm,

    // Comment actions
    addComment,
    updateComment,
    addReply,
    deleteComment,
    deleteReply,
    setStatus,
    resolveComment,

    // Navigation
    setActiveComment: store.setActiveComment,
    navigateToComment,

    // Event handlers
    handleCommentActivated,
  }
}

export default useComments
